import { Platform } from 'react-native';

import {
    parsePaperPdfAvailabilityResponse,
    pdfAvailabilityEndpointCandidates,
} from './pdfAvailabilityApiContract';
import type { PaperPdfAvailabilityResponse } from './pdfAvailabilityApiContract';

const REQUEST_TIMEOUT_MS = 20_000;

export async function refreshPaperPdfAvailability({
    paperId,
    accessToken,
}: {
    paperId: string;
    accessToken: string;
}): Promise<PaperPdfAvailabilityResponse> {
    const hostname = Platform.OS === 'web' && typeof window !== 'undefined'
        ? window.location.hostname
        : undefined;
    const endpoints = pdfAvailabilityEndpointCandidates({
        paperId,
        isWeb: Platform.OS === 'web',
        hostname,
    });
    let lastError: Error | null = null;

    for (const endpoint of endpoints) {
        const controller = typeof AbortController !== 'undefined'
            ? new AbortController()
            : null;
        const timeout = controller
            ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
            : null;
        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                cache: 'no-store',
                ...(controller ? { signal: controller.signal } : {}),
            });
            if (!response.ok) {
                throw new Error(`PDF availability request failed with status ${response.status}.`);
            }

            return parsePaperPdfAvailabilityResponse(await response.json(), paperId);
        } catch (error) {
            lastError = error instanceof Error
                ? error
                : new Error('PDF availability request failed.');
        } finally {
            if (timeout) {
                clearTimeout(timeout);
            }
        }
    }

    throw lastError || new Error('PDF availability request failed.');
}
